import React, { useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { Input, SuccessMessage, ErrorMessage } from '../../UI';
import {
  Settings,
  Shield,
  Bell,
  Database,
  Mail,
  Globe,
  Lock,
  Save,
  RefreshCw
} from 'lucide-react';
import { PageHeader, PrimaryButton } from './theme';

const SuperAdminSettingsPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const [settings, setSettings] = useState({
    general: {
      siteName: 'CustomerTrack',
      siteUrl: 'https://customertrack.app',
      supportEmail: 'support@customertrack.app',
      timezone: 'Asia/Kolkata'
    },
    security: {
      sessionTimeout: 30,
      maxLoginAttempts: 5,
      passwordExpiry: 90,
      twoFactorEnabled: false
    },
    notifications: {
      emailNotifications: true,
      systemAlerts: true,
      newClientAlerts: true,
      subscriptionAlerts: true
    },
    maintenance: {
      maintenanceMode: false,
      maintenanceMessage: 'System is under maintenance. Please try again later.'
    }
  });

  const handleSave = async () => {
    setLoading(true);
    try {
      // API call to save settings would go here
      await new Promise(resolve => setTimeout(resolve, 1000));
      setSuccess('Settings saved successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'general', label: 'General', icon: Globe },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'maintenance', label: 'Maintenance', icon: Database }
  ];

  return (
    <div className="space-y-6">
      {success && <SuccessMessage message={success} onClose={() => setSuccess('')} />}
      {error && <ErrorMessage message={error} onClose={() => setError('')} />}

      <PageHeader
        title="Settings"
        subtitle="Global platform preferences applied across every client organization"
        actions={
          <PrimaryButton icon={Save} onClick={handleSave} disabled={loading}>
            {loading ? 'Saving…' : 'Save changes'}
          </PrimaryButton>
        }
      />

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Tabs Sidebar */}
        <div className="lg:w-64 flex-shrink-0">
          <div className="bg-white rounded-xl border border-gray-200 p-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                  activeTab === tab.id
                    ? 'bg-blue-50 text-blue-600'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                <span className="font-medium">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Settings Content */}
        <div className="flex-1">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            {/* General Settings */}
            {activeTab === 'general' && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 pb-4 border-b">
                  <Globe className="w-6 h-6 text-blue-600" />
                  <h3 className="text-lg font-semibold">General Settings</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Input
                    label="Site Name"
                    value={settings.general.siteName}
                    onChange={(e) => setSettings({
                      ...settings,
                      general: { ...settings.general, siteName: e.target.value }
                    })}
                  />
                  <Input
                    label="Site URL"
                    value={settings.general.siteUrl}
                    onChange={(e) => setSettings({
                      ...settings,
                      general: { ...settings.general, siteUrl: e.target.value }
                    })}
                  />
                  <Input
                    label="Support Email"
                    type="email"
                    value={settings.general.supportEmail}
                    onChange={(e) => setSettings({
                      ...settings,
                      general: { ...settings.general, supportEmail: e.target.value }
                    })}
                  />
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Timezone</label>
                    <select
                      value={settings.general.timezone}
                      onChange={(e) => setSettings({
                        ...settings,
                        general: { ...settings.general, timezone: e.target.value }
                      })}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
                      <option value="UTC">UTC</option>
                      <option value="America/New_York">America/New_York (EST)</option>
                      <option value="Europe/London">Europe/London (GMT)</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Security Settings */}
            {activeTab === 'security' && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 pb-4 border-b">
                  <Shield className="w-6 h-6 text-green-600" />
                  <h3 className="text-lg font-semibold">Security Settings</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Input
                    label="Session Timeout (minutes)"
                    type="number"
                    value={settings.security.sessionTimeout}
                    onChange={(e) => setSettings({
                      ...settings,
                      security: { ...settings.security, sessionTimeout: parseInt(e.target.value) }
                    })}
                  />
                  <Input
                    label="Max Login Attempts"
                    type="number"
                    value={settings.security.maxLoginAttempts}
                    onChange={(e) => setSettings({
                      ...settings,
                      security: { ...settings.security, maxLoginAttempts: parseInt(e.target.value) }
                    })}
                  />
                  <Input
                    label="Password Expiry (days)"
                    type="number"
                    value={settings.security.passwordExpiry}
                    onChange={(e) => setSettings({
                      ...settings,
                      security: { ...settings.security, passwordExpiry: parseInt(e.target.value) }
                    })}
                  />
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Two-Factor Authentication</label>
                    <div className="flex items-center gap-3 mt-2">
                      <button
                        onClick={() => setSettings({
                          ...settings,
                          security: { ...settings.security, twoFactorEnabled: !settings.security.twoFactorEnabled }
                        })}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          settings.security.twoFactorEnabled ? 'bg-blue-600' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            settings.security.twoFactorEnabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                      <span className="text-sm text-gray-600">
                        {settings.security.twoFactorEnabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <div className="flex items-start gap-3">
                    <Lock className="w-5 h-5 text-yellow-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-yellow-800">Security Recommendation</p>
                      <p className="text-sm text-yellow-700 mt-1">
                        Enable two-factor authentication for enhanced security across all admin accounts.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Notification Settings */}
            {activeTab === 'notifications' && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 pb-4 border-b">
                  <Bell className="w-6 h-6 text-purple-600" />
                  <h3 className="text-lg font-semibold">Notification Settings</h3>
                </div>

                <div className="space-y-4">
                  {[
                    { key: 'emailNotifications', label: 'Email Notifications', desc: 'Receive important updates via email' },
                    { key: 'systemAlerts', label: 'System Alerts', desc: 'Get notified about system issues and updates' },
                    { key: 'newClientAlerts', label: 'New Client Alerts', desc: 'Notify when new clients register' },
                    { key: 'subscriptionAlerts', label: 'Subscription Alerts', desc: 'Alerts for subscription changes and renewals' }
                  ].map((item) => (
                    <div key={item.key} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">{item.label}</p>
                        <p className="text-sm text-gray-500">{item.desc}</p>
                      </div>
                      <button
                        onClick={() => setSettings({
                          ...settings,
                          notifications: { ...settings.notifications, [item.key]: !settings.notifications[item.key] }
                        })}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          settings.notifications[item.key] ? 'bg-blue-600' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            settings.notifications[item.key] ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Maintenance Settings */}
            {activeTab === 'maintenance' && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 pb-4 border-b">
                  <Database className="w-6 h-6 text-orange-600" />
                  <h3 className="text-lg font-semibold">Maintenance Settings</h3>
                </div>

                <div className="space-y-6">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">Maintenance Mode</p>
                      <p className="text-sm text-gray-500">Temporarily disable access for all users except superadmins</p>
                    </div>
                    <button
                      onClick={() => setSettings({
                        ...settings,
                        maintenance: { ...settings.maintenance, maintenanceMode: !settings.maintenance.maintenanceMode }
                      })}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        settings.maintenance.maintenanceMode ? 'bg-red-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          settings.maintenance.maintenanceMode ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Maintenance Message</label>
                    <textarea
                      value={settings.maintenance.maintenanceMessage}
                      onChange={(e) => setSettings({
                        ...settings,
                        maintenance: { ...settings.maintenance, maintenanceMessage: e.target.value }
                      })}
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <button className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors">
                      <RefreshCw className="w-5 h-5 text-gray-500" />
                      <span className="font-medium text-gray-700">Clear Cache</span>
                    </button>
                    <button className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors">
                      <Database className="w-5 h-5 text-gray-500" />
                      <span className="font-medium text-gray-700">Backup Database</span>
                    </button>
                  </div>
                </div>

                {settings.maintenance.maintenanceMode && (
                  <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                    <div className="flex items-start gap-3">
                      <Database className="w-5 h-5 text-red-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-red-800">Maintenance Mode Active</p>
                        <p className="text-sm text-red-700 mt-1">
                          The system is currently in maintenance mode. Only superadmins can access the system.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminSettingsPage;
